import {
  DEFAULT_TYPOGRAPHY,
  typographySchema,
  type DisplayFont,
  type DisplayFontSize,
  type Typography,
} from '@gb-ticket/shared-types';

/** Browser copy of the pair that is currently on the document. */
export const TYPOGRAPHY_STORAGE_KEY = 'gb.typography.v1';

/** Multiplies text only. The document root stays at 16px so frames in rem do not move. */
export const TEXT_SCALE_PROPERTY = '--gb-text-scale';

export const LOGIN_PATH = '/anmelden';

const SYSTEM_STACK = 'ui-sans-serif, system-ui, sans-serif';

const NAMED_STACKS: Record<Exclude<DisplayFont, 'system'>, string> = {
  inter: `"Inter", ${SYSTEM_STACK}`,
  'source-sans-3': `"Source Sans 3", ${SYSTEM_STACK}`,
  'ibm-plex-sans': `"IBM Plex Sans", ${SYSTEM_STACK}`,
  'noto-sans': `"Noto Sans", ${SYSTEM_STACK}`,
};

/** Family used inside the preview. System is an explicit stack so the card does not inherit a custom face. */
export function fontFamilyFor(font: DisplayFont): string {
  return font === 'system' ? SYSTEM_STACK : NAMED_STACKS[font];
}

export function formatFontSizeLabel(size: DisplayFontSize): string {
  return `${size.replace('.', ',')} px`;
}

/** 16px is scale 1. The step values divide evenly, so the result is exact. */
export function textScaleFor(size: DisplayFontSize): string {
  return String(Number(size) / 16);
}

type TypographyStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function readTypography(storage: TypographyStorage | null): Typography | null {
  if (!storage) {
    return null;
  }
  let raw: string | null;
  try {
    raw = storage.getItem(TYPOGRAPHY_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw == null) {
    return null;
  }
  try {
    const parsed = typographySchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : { ...DEFAULT_TYPOGRAPHY };
  } catch {
    return { ...DEFAULT_TYPOGRAPHY };
  }
}

export function writeTypography(storage: TypographyStorage | null, value: Typography): void {
  try {
    storage?.setItem(TYPOGRAPHY_STORAGE_KEY, JSON.stringify(typographySchema.parse(value)));
  } catch {
    // Private mode: the document can still show the pair for this visit.
  }
}

export function clearTypography(storage: TypographyStorage | null): void {
  try {
    storage?.removeItem(TYPOGRAPHY_STORAGE_KEY);
  } catch {
    // Ignore storage that refuses the delete.
  }
}

export function resolveTypography(
  profile:
    | {
        displayFont: DisplayFont | null;
        displayFontSize: DisplayFontSize | null;
      }
    | null
    | undefined,
  brand: Typography | null | undefined,
): Typography {
  if (profile?.displayFont && profile.displayFontSize) {
    return { displayFont: profile.displayFont, displayFontSize: profile.displayFontSize };
  }
  if (brand) {
    return { displayFont: brand.displayFont, displayFontSize: brand.displayFontSize };
  }
  return { ...DEFAULT_TYPOGRAPHY };
}

/**
 * Scales text through `--gb-text-scale` and leaves the root font size untouched.
 * An older inline root size is removed so frames stay at 16px.
 * System removes a custom family; the other four set one.
 */
export function applyTypography(root: HTMLElement, value: Typography): void {
  root.style.removeProperty('font-size');
  root.style.setProperty(TEXT_SCALE_PROPERTY, textScaleFor(value.displayFontSize));
  if (value.displayFont === 'system') {
    root.style.removeProperty('font-family');
    return;
  }
  root.style.fontFamily = fontFamilyFor(value.displayFont);
}

export function browserTypographyStorage(): TypographyStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** First paint. The login page waits for the public brand and ignores this key. */
export function applyStoredTypography(root: HTMLElement, pathname: string): void {
  if (pathname === LOGIN_PATH) {
    return;
  }
  const stored = readTypography(browserTypographyStorage());
  if (!stored) {
    return;
  }
  applyTypography(root, stored);
}
